import React, { useState } from "react";
import styled from "styled-components";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Diploma } from "@domain/organization/track/diploma";
import { Track, TrackLevel } from "@domain/organization/track/track";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Clamp } from "@ui/components/containers/clamp";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { Primary, PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ChevronDownIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { useModalTools } from "@ui/modals/modalContext";
import { gap, mobile } from "@ui/styles/utils";
import { DiplomaEditorModal } from "./diplomaEditorModal";

interface TrackLevelViewProps extends React.HTMLAttributes<HTMLDivElement> {
	track: Track;
	level: TrackLevel;
	onDiplomaSelected: (diploma: Diploma) => void;
}
export const TrackLevelView: React.FC<TrackLevelViewProps> = ({ track, level, onDiplomaSelected, ...htmlProps }) => {
	const alias = useOrganizationAlias();
	const [opened, setOpened] = useState(false);
	const { createModal } = useModalTools();
	const canOpen = level.diplomas.size > 0;

	return (
		<Container {...htmlProps}>
			<Level opened={opened} onClick={() => setOpened(canOpen && !opened)} canOpen={canOpen}>
				<Primary>{level.title}</Primary>
				{alias && (
					<RoundedPlusButton
						onClick={() =>
							createModal((close) => <DiplomaEditorModal track={track} levelId={level.id} close={close} />)
						}
					/>
				)}
				<ChevronDownIcon />
			</Level>
			{opened && (
				<div>
					{level.diplomas.map((diploma) => (
						<DiplomaView onClick={() => onDiplomaSelected(diploma)} key={diploma.id}>
							<PrimaryBold>{diploma.title}</PrimaryBold>
							<Primary>
								<Clamp>
									<DraftRenderer content={diploma.description} />
								</Clamp>
							</Primary>
						</DiplomaView>
					))}
				</div>
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)``;

const Level = styled(RoundedBlock)<{ opened: boolean; canOpen: boolean }>`
	cursor: pointer;

	${({ canOpen }) => !canOpen && "cursor: initial"};
	${row(10, "space-between", "center")};
	padding: 15px 35px;
	@media ${mobile} {
		padding: 15px 20px;
	}
	border-top: none;
	margin-left: -1px;
	margin-right: -1px;
	${({ opened }) => !opened && "border-bottom: none"};

	> svg {
		flex-shrink: 0;
		${({ opened }) => opened && "transform: rotate(180deg)"};
		path {
			${({ canOpen, theme }) => !canOpen && `fill: ${theme.colors.lightgray}`};
		}
	}

	a {
		pointer-events: all;
		:hover div {
			color: ${({ theme }) => theme.colors.primary};
		}
	}

	span {
		color: ${({ theme }) => theme.colors.secondary};
	}
`;

const DiplomaView = styled(Primary)`
	padding: 20px 35px;
	${gap(8)};
	@media (max-width: 900px) {
		padding: 15px 20px;
	}
	cursor: pointer;
	> :first-child {
		color: ${({ theme }) => theme.colors.secondary};
	}
	:hover > :first-child {
		color: ${({ theme }) => theme.colors.primary};
	}
	&:not(:last-child) {
		border-bottom: 1px solid ${({ theme }) => theme.colors.lightgray};
	}
`;
